/**
 * 图片压缩工具模块
 * 提供图片压缩、尺寸调整、格式转换等功能
 */

// 调整为更优的压缩参数 - 全局统一配置
const COMPRESSION_CONFIG = {
  maxWidth: 800,           // 提升清晰度
  maxHeight: 800,
  quality: 0.7,            // 平衡质量和大小
  format: 'image/webp' as const,     // 更好的压缩比
  targetSizeKB: 100,       // 更小的目标大小
  maintainAspectRatio: true
}

/**
 * Replace the last extension on a filename.
 * `photo.PNG` -> `photo.webp`, `foo.tar.gz` -> `foo.tar.webp`, `noext` -> `noext.webp`.
 */
export function replaceExt(name: string, newExt: string): string {
  const cleanExt = newExt.startsWith('.') ? newExt.slice(1) : newExt
  const lastDot = name.lastIndexOf('.')
  if (lastDot <= 0) return `${name}.${cleanExt}`
  return `${name.slice(0, lastDot)}.${cleanExt}`
}

export interface CompressOptions {
  /** 按长边缩（宽、高哪个长限制哪个）；给了就不看 maxWidth / maxHeight */
  longEdge?: number
  maxWidth?: number
  maxHeight?: number
  quality?: number
  outputFormat?: 'image/jpeg' | 'image/png' | 'image/webp'
  maxSizeKB?: number
}

export interface CompressResult {
  blob: Blob
  width: number
  height: number
  originalWidth: number
  originalHeight: number
  originalSize: number
  compressedSize: number
  compressionRatio: number
}

/**
 * 计算缩放后的尺寸，保持宽高比
 * 优先限制宽度不超过maxWidth，高度按比例缩放
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight?: number
): { width: number; height: number } {
  // 如果原图宽度已经小于等于最大宽度，直接返回原尺寸
  if (originalWidth <= maxWidth) {
    return { width: originalWidth, height: originalHeight }
  }

  // 计算缩放比例（基于宽度）
  const scale = maxWidth / originalWidth
  const width = maxWidth
  const height = Math.round(originalHeight * scale)

  // 如果设置了最大高度且超过限制，则基于高度重新计算
  if (maxHeight && height > maxHeight) {
    const heightScale = maxHeight / originalHeight
    return {
      width: Math.round(originalWidth * heightScale),
      height: maxHeight
    }
  }

  return { width, height }
}

/** 按长边缩放：长边不超过 longEdge，不放大 */
export function longEdgeDimensions(originalWidth: number, originalHeight: number, longEdge: number): { width: number; height: number } {
  const long = Math.max(originalWidth, originalHeight)
  if (long <= longEdge) return { width: originalWidth, height: originalHeight }
  const scale = longEdge / long
  return { width: Math.round(originalWidth * scale), height: Math.round(originalHeight * scale) }
}

/**
 * 将文件转换为图片元素
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }

    img.src = url
  })
}

/**
 * 压缩图片到指定大小以下
 */
async function compressToTargetSize(
  canvas: HTMLCanvasElement,
  format: string,
  maxSizeKB: number
): Promise<Blob> {
  let quality = 0.9
  let blob = await canvasToBlob(canvas, format, quality)

  const maxSizeBytes = maxSizeKB * 1024

  // 逐步降低质量直到满足大小要求
  while (blob.size > maxSizeBytes && quality > 0.1) {
    quality -= 0.1
    blob = await canvasToBlob(canvas, format, quality)
  }

  // 如果还是太大，尝试缩小尺寸
  if (blob.size > maxSizeBytes && canvas.width > 400) {
    const scale = 0.8
    const newCanvas = document.createElement('canvas')
    const newCtx = newCanvas.getContext('2d')

    newCanvas.width = Math.round(canvas.width * scale)
    newCanvas.height = Math.round(canvas.height * scale)

    if (newCtx) {
      newCtx.drawImage(canvas, 0, 0, newCanvas.width, newCanvas.height)
      blob = await compressToTargetSize(newCanvas, format, maxSizeKB)
    }
  }

  return blob
}

/**
 * Canvas转Blob辅助函数
 */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  format: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to convert canvas to blob'))
        }
      },
      format,
      quality
    )
  })
}

/**
 * 主压缩函数
 */
export async function compressImage(
  file: File,
  options: CompressOptions = {}
): Promise<CompressResult> {
  // 使用全局统一配置作为默认值
  const {
    longEdge,
    maxWidth = COMPRESSION_CONFIG.maxWidth,
    maxHeight = COMPRESSION_CONFIG.maxHeight,
    quality = COMPRESSION_CONFIG.quality,
    outputFormat = COMPRESSION_CONFIG.format,
    maxSizeKB = COMPRESSION_CONFIG.targetSizeKB
  } = options

  try {
    // 加载图片
    const img = await loadImage(file)
    const originalWidth = img.naturalWidth
    const originalHeight = img.naturalHeight

    // 计算目标尺寸（保持宽高比）
    const { width, height } = longEdge
      ? longEdgeDimensions(originalWidth, originalHeight, longEdge)
      : calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight)

    // 创建canvas进行压缩
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('Failed to get canvas context')
    }

    canvas.width = width
    canvas.height = height

    // 设置白色背景（对于透明图片转JPEG）
    if (outputFormat === 'image/jpeg') {
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, width, height)
    }

    // 绘制图片
    ctx.drawImage(img, 0, 0, width, height)

    // 转换为Blob
    let blob: Blob

    if (maxSizeKB) {
      blob = await compressToTargetSize(canvas, outputFormat, maxSizeKB)
    } else {
      blob = await canvasToBlob(canvas, outputFormat, quality)
    }

    return {
      blob,
      width,
      height,
      originalWidth,
      originalHeight,
      originalSize: file.size,
      compressedSize: blob.size,
      compressionRatio: blob.size / file.size
    }
  } catch (error) {
    console.error('Image compression failed:', error)
    throw error
  }
}

/**
 * 检查文件是否为图片
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/')
}

/**
 * 获取图片尺寸
 */
export async function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  const img = await loadImage(file)
  return {
    width: img.naturalWidth,
    height: img.naturalHeight
  }
}

/**
 * 格式化文件大小
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Wraps a compressed Blob as a File with image/webp MIME type and a .webp filename.
 * The original filename's extension is replaced with `.webp` via replaceExt.
 *
 * @param blob - The compressed image blob (expected to be image/webp content).
 * @param originalName - The original file name; its extension will be replaced with `.webp`.
 * @returns A new File instance with type `image/webp` and a `.webp` filename.
 *
 * This is an internal helper used by prepareImageForUpload. External callers
 * should normally use prepareImageForUpload instead, which also handles HEIC
 * rejection and non-image passthrough.
 */
export function wrapAsWebpFile(blob: Blob, originalName: string): File {
  return new File([blob], replaceExt(originalName, 'webp'), {
    type: 'image/webp',
    lastModified: Date.now(),
  })
}

/** 建产品时的实物照片：长边 720、质量 0.5（同手机） */
export const PRODUCT_PHOTO_COMPRESSION: CompressOptions = { longEdge: 720, quality: 0.5, maxSizeKB: 0 }
/** 网页截图：长边 1568（读图模型的最大有效尺寸）、质量 0.7，价格和小字要看得清 */
export const SCREENSHOT_COMPRESSION: CompressOptions = { longEdge: 1568, quality: 0.7, maxSizeKB: 0 }

/**
 * Single entry point for preparing a user-selected file for upload.
 * - Non-images: returned as-is.
 * - HEIC/HEIF: throws UNSUPPORTED_FORMAT (browsers can't canvas-decode it).
 * - Other images: compressed to WebP (see COMPRESSION_CONFIG) and re-wrapped
 *   as a File with type=image/webp and a .webp filename.
 */
export async function prepareImageForUpload(file: File, options?: CompressOptions): Promise<File> {
  const lowerName = file.name.toLowerCase()
  const isHeic =
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    lowerName.endsWith('.heic') ||
    lowerName.endsWith('.heif')

  // HEIC check must precede non-image passthrough: a .heic file with a
  // non-image MIME (e.g. application/octet-stream) would otherwise slip
  // through isImageFile() === false and bypass rejection.
  if (isHeic) {
    throw new Error('UNSUPPORTED_FORMAT: HEIC/HEIF is not supported. Please convert to JPG or PNG.')
  }

  if (!isImageFile(file)) {
    return file
  }

  const result = await compressImage(file, options)
  return wrapAsWebpFile(result.blob, file.name)
}
